import { Flex } from 'antd';
import CardDetailButton from '../../cardDetailButton/cardDetailButton';
import InteractiveCard from '../../common/interactiveCard';
import { useTranslation } from 'react-i18next';
import { Divider, Heading, Paragraph } from '../../Ant';
import { Box, Coin, DollarCircle, User } from '../../icons';
import CardData from '../../dashboard/cardData';
import {
  borderColorMapping,
  expenseStatusTranslationMapping,
  tagColorMapping,
} from '../../../constants/expense';
import { IExpense } from '../../../interface/expense';
import { ROUTES, SystemConstants } from '../../../constants/constants';
import { isEqual } from 'lodash';
import { formatter } from '../../../constants/utilityFunctions';
import { useTransactionParameters } from '../../../context/TransactionParameterContext';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import NoteIcons from '../../common/NoteIcons';

const ExpenseCard = ({ expense }: { expense: IExpense }) => {
  const { t } = useTranslation();
  const { parameters } = useTransactionParameters();
  const { push } = useNavigationStack();
  const baseCurrency = parameters?.currency;
  const priceLabel = isEqual(expense?.unit?.id, SystemConstants.UNIT_HOUR)
    ? t('expense:hourPrice')
    : t('expense:unitPrice');
  const handleExpenseView = () => {
    push(ROUTES.EXPENSE_DETAIL + `/${expense?.id}`);
  };
  return (
    <InteractiveCard
      data-testid="expense-card"
      className={`pointer w-full customer-card ${borderColorMapping[expense?.status]}`}
      onActivate={handleExpenseView}
    >
      <Flex justify="space-between" align="center" className="pb-10">
        <Flex gap={8} align="center">
          <Flex
            justify="center"
            align="center"
            className={`status-chip ${tagColorMapping[expense?.status]}`}
          >
            {t(expenseStatusTranslationMapping[expense?.status])}
          </Flex>
          <NoteIcons hasImportantNotes={expense?.hasImportantNotes} hasNotes={expense?.hasNotes} />
        </Flex>

        <Flex gap={10}>
          <CardDetailButton onOpen={handleExpenseView} testId="expense-card-go-detail" />
        </Flex>
      </Flex>
      <Divider className="mb-10" />
      <Flex vertical gap={10}>
        <CardData
          icon={<Coin />}
          title={t('expense:expense')}
          content={expense?.subject}
          contentclass="fw-400 lineclamp-2"
        />
        <CardData
          icon={<User />}
          title={t('common:author')}
          content={expense?.expenseUser?.fullName ?? '-'}
        />

        <Flex justify="space-between">
          <CardData
            icon={<DollarCircle />}
            title={`${priceLabel} (${baseCurrency ?? 'EUR'})`}
            content={formatter.regionalNumberFormat({
              value: expense?.unitPriceNet ?? 0,
              minimumFractionDigits: 2,
            })}
          />
          <CardData
            type="price"
            icon={<Box />}
            title={t('expense:units')}
            content={expense?.units ?? 0}
          />
        </Flex>
        <Divider />
        <Flex justify="space-between" align="center">
          <Paragraph $color="title" $lineheight="20px" $fontsize="13px" $weight={500}>
            {`${t('expense:totalPrice')} (${baseCurrency ?? 'EUR'})`}
          </Paragraph>
          <Heading level={6} $color="title" $lineheight="20px" $fontsize="13px" $weight={700}>
            {formatter.regionalNumberFormat({
              value: expense?.salesPriceNet ?? 0,
              minimumFractionDigits: 2,
            })}
          </Heading>
        </Flex>
      </Flex>
    </InteractiveCard>
  );
};

export default ExpenseCard;
